import React, { InputHTMLAttributes } from 'react'
import MaskedInput from 'react-text-mask'
import theme from 'presentation/styles/theme'
import { ReactComponent as CloseIcon } from 'presentation/assets/icons/new_close.svg'
import * as S from './styles'

export type TextFieldProps = {
  initialValue?: string | number | undefined
  label?: string
  labelColor?: keyof typeof theme.ds.colors
  labelSize?: keyof typeof theme.ds.typography.size
  labelWeight?: keyof typeof theme.ds.typography.weight
  labelIcon?: React.ReactNode
  onLabelIconClick?: () => void
  icon?: React.ReactNode
  iconPosition?: 'insideRight' | 'outsideRight' | 'insideLeft' | 'outsideLeft'
  iconIsButton?: boolean
  iconColor?: keyof typeof theme.ds.colors
  iconBgColor?: keyof typeof theme.ds.colors
  onIconClick?: (param?: any) => void
  onInputChange?: (value: string) => void
  disabled?: boolean
  error?: string | false
  bgColor?: keyof typeof theme.ds.colors
  radius?: keyof typeof theme.ds.typography.border.radius
  required?: boolean
  requiredColor?: keyof typeof theme.ds.colors
  guide?: boolean
  mask?: (string | RegExp)[] | ((value: string) => (string | RegExp)[])
  regex?: RegExp
  onResetTableValue?: () => any
  labelOverlapping?: boolean
  baselineStyle?: boolean
  keepStyleOnIconIsButton?: boolean
  clearable?: boolean
  onClearClick?: (param?: any) => void
  height?: string
} & InputHTMLAttributes<HTMLInputElement>

const TextFieldNew: React.ForwardRefRenderFunction<
  HTMLInputElement,
  TextFieldProps
> = (
  {
    label,
    labelSize = 'small',
    labelWeight = 'normal',
    keepStyleOnIconIsButton = false,
    requiredColor = 'danger200',
    name,
    mask,
    error,
    disabled = false,
    onInputChange,
    style,
    bgColor = 'white',
    radius = 'xxsmall',
    icon,
    iconPosition = 'insideRight',
    iconIsButton = false,
    iconColor = 'primary600',
    iconBgColor = 'primary600',
    onIconClick,
    required = false,
    labelColor = 'neutral900',
    onResetTableValue,
    labelIcon,
    onLabelIconClick,
    guide,
    regex,
    labelOverlapping = false,
    baselineStyle = false,
    clearable = false,
    onClearClick,
    height = '48px',
    ...props
  }: TextFieldProps,
  ref
) => {
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value
    if (onResetTableValue && newValue === '') onResetTableValue()

    if (newValue && regex && !regex.test(newValue)) {
      return
    }

    !!onInputChange && onInputChange(newValue)
    !!props.onChange && props.onChange(e)
  }

  const iconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    !!onIconClick && onIconClick(e)
  }

  const labelIconClick = () => {
    !!onLabelIconClick && onLabelIconClick()
  }

  const clearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    !!onClearClick && onClearClick(e)
  }

  const showClearButton =
    clearable && iconPosition !== 'insideRight' && props.value

  return (
    <S.Wrapper
      style={style}
      disabled={disabled}
      error={!!error}
      baselineStyle={baselineStyle}
    >
      {(!!label || !!labelIcon) && (
        <S.LabelWrapper>
          {!!label && (
            <S.Label
              htmlFor={name}
              labelColor={labelColor}
              labelSize={labelSize}
              labelWeight={labelWeight}
              labelOverlapping={labelOverlapping}
              baselineStyle={baselineStyle}
              disabled={disabled}
            >
              {label}
              {required ? (
                <S.Asterisk requiredColor={requiredColor}>*</S.Asterisk>
              ) : (
                ''
              )}
            </S.Label>
          )}
          {!!labelIcon && (
            <S.LabelIcon onClick={labelIconClick}>{labelIcon}</S.LabelIcon>
          )}
        </S.LabelWrapper>
      )}
      <S.InputWrapper baselineStyle={baselineStyle}>
        {!!icon && (
          <S.Icon
            type="button"
            onClick={iconClick}
            iconColor={iconColor}
            iconBgColor={iconBgColor}
            iconPosition={iconPosition}
            iconIsButton={iconIsButton}
            keepStyleOnIconIsButton={keepStyleOnIconIsButton}
            radius={radius}
            height={height}
            disabled={disabled || !iconIsButton}
          >
            {icon}
          </S.Icon>
        )}
        {mask ? (
          <MaskedInput
            type="text"
            mask={mask}
            guide={guide ?? true}
            disabled={disabled}
            name={name}
            {...props}
            onChange={onChange}
            render={(ref, props) => (
              <S.Input
                ref={(input) => ref(input!)}
                bgColor={bgColor}
                radius={radius}
                icon={icon}
                iconPosition={iconPosition}
                baselineStyle={baselineStyle}
                clearable={clearable}
                height={height}
                {...(label ? { id: name } : {})}
                {...props}
              />
            )}
          />
        ) : (
          <S.Input
            type="text"
            disabled={disabled}
            name={name}
            bgColor={bgColor}
            radius={radius}
            ref={ref}
            icon={icon}
            iconPosition={iconPosition}
            baselineStyle={baselineStyle}
            clearable={clearable}
            height={height}
            {...(label ? { id: name } : {})}
            {...props}
            onChange={onChange}
          />
        )}
        {showClearButton && (
          <S.ClearButton
            type="button"
            onClick={clearClick}
            disabled={disabled}
            radius={radius}
            height={height}
          >
            <CloseIcon />
          </S.ClearButton>
        )}
      </S.InputWrapper>
      {!!error && <S.Error className="error">{error}</S.Error>}
    </S.Wrapper>
  )
}

export default React.forwardRef(TextFieldNew)
